import { MapPin, Phone, Calendar, ExternalLink, Sparkles, UserCheck, UserX } from 'lucide-react';
import { Badge, getCuisineBadgeVariant } from '../common/Badge';
import { Button } from '../common/Button';
import type { Lead } from '../../api/client';
import clsx from 'clsx';

interface LeadCardProps {
  lead: Lead;
  currentUserEmail?: string;
  onClick?: () => void;
  onClaim?: (id: number) => void;
  onRelease?: (id: number) => void;
  isClaimLoading?: boolean;
  showAssignments?: boolean;
}

export function LeadCard({
  lead,
  currentUserEmail,
  onClick,
  onClaim,
  onRelease,
  isClaimLoading,
  showAssignments = true
}: LeadCardProps) {
  const formattedDate = lead.first_seen_at
    ? new Date(lead.first_seen_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const isClaimedByMe = lead.assigned_to === currentUserEmail;
  const isClaimedByOther = lead.assigned_to && !isClaimedByMe;

  const handleClaimClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClaim) {
      onClaim(lead.id);
    }
  };

  const handleReleaseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRelease) {
      onRelease(lead.id);
    }
  };

  return (
    <div
      onClick={onClick}
      className={clsx(
        'card card-hover p-4 cursor-pointer relative',
        lead.is_new && 'ring-2 ring-brand-secondary',
        showAssignments && isClaimedByMe && 'bg-green-50 border-green-200',
        showAssignments && isClaimedByOther && 'bg-gray-50 opacity-75'
      )}
    >
      {/* Claimed indicator - only show if showAssignments is true */}
      {showAssignments && lead.assigned_to && (
        <div className={clsx(
          'absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1',
          isClaimedByMe ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
        )}>
          <UserCheck className="w-3 h-3" />
          {isClaimedByMe ? 'Mine' : lead.assigned_to.split('@')[0]}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-16">
          <div className="flex items-center space-x-2 mb-1">
            {lead.is_new && (
              <Sparkles className="w-4 h-4 text-brand-secondary flex-shrink-0" />
            )}
            <h3 className="font-semibold text-font-primary truncate">{lead.name}</h3>
          </div>
          <p className="text-sm text-font-secondary truncate">{lead.region}</p>
        </div>
        <ExternalLink className="w-4 h-4 text-font-secondary flex-shrink-0 ml-2 mt-6" />
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {lead.is_new && <Badge variant="new">New</Badge>}
        {lead.cuisine_detected && (
          <Badge variant={getCuisineBadgeVariant(lead.cuisine_detected)}>
            {lead.cuisine_detected}
          </Badge>
        )}
        <Badge variant="status" statusType={lead.status}>
          {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
        </Badge>
      </div>

      {/* Details */}
      <div className="space-y-1.5 text-sm">
        {lead.address && (
          <div className="flex items-start space-x-2 text-font-secondary">
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">
              {lead.address}
              {lead.city && `, ${lead.city}`}
              {lead.state && ` ${lead.state}`}
              {lead.zipcode && ` ${lead.zipcode}`}
            </span>
          </div>
        )}

        {lead.phone && (
          <div className="flex items-center space-x-2 text-font-secondary">
            <Phone className="w-4 h-4 flex-shrink-0" />
            <span>{lead.phone}</span>
          </div>
        )}

        {formattedDate && (
          <div className="flex items-center space-x-2 text-font-secondary">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span>First seen: {formattedDate}</span>
          </div>
        )}
      </div>

      {/* License info */}
      {lead.license_type && (
        <div className="mt-3 pt-3 border-t border-card-border">
          <p className="text-xs text-font-secondary">
            {lead.license_type}
            {lead.license_status && ` - ${lead.license_status}`}
          </p>
        </div>
      )}

      {/* Claim/Release buttons - only show if showAssignments is true */}
      {showAssignments && currentUserEmail && (onClaim || onRelease) && (
        <div className="mt-3 pt-3 border-t border-card-border">
          {!lead.assigned_to && onClaim && (
            <Button
              size="sm"
              onClick={handleClaimClick}
              disabled={isClaimLoading}
              className="w-full"
            >
              <UserCheck className="w-4 h-4 mr-1" />
              Claim Lead
            </Button>
          )}
          {isClaimedByMe && onRelease && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleReleaseClick}
              disabled={isClaimLoading}
              className="w-full"
            >
              <UserX className="w-4 h-4 mr-1" />
              Release Lead
            </Button>
          )}
          {isClaimedByOther && (
            <p className="text-xs text-center text-gray-500">
              Claimed by {lead.assigned_to}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
